import React, {useRef, useLayoutEffect} from 'react';

import {faTimes} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from '@newsela/angelou';
import {css} from 'emotion';
import {trim, forEach} from 'lodash-es';
import PropTypes from 'prop-types';

import {ButtonWithTrackEvent} from 'static/three-oh/src/components/Button/Button.jsx';
import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';
import {ENTER, DOWN_ARROW, ESCAPE, TAB} from 'static/three-oh/src/constants/keyCodes.js';

import {animationTimeout} from '../../../constants';
import {GLOBAL_NAV_SEARCH_INPUT_FIELD_ID} from '../constants';
import styles from './styles';

const AngelouButtonWithTrackEvent = withTrackEvent(Button);

const propTypes = {
  history: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  userProperties: PropTypes.object.isRequired,
  setVisibilityOfSuggestions: PropTypes.func.isRequired,
  setActiveStateOfSuggestions: PropTypes.func.isRequired,
  setThrottledQueryTerm: PropTypes.func.isRequired,
  onQueryTermSubmission: PropTypes.func.isRequired,
  queryTerm: PropTypes.string.isRequired,
  setQueryTerm: PropTypes.func.isRequired,
  handleOnEscape: PropTypes.func.isRequired,
  handleOnCancel: PropTypes.func.isRequired,
  isDesktopDisplay: PropTypes.bool.isRequired,
};

const defaultProps = {
  isDesktopDisplay: false,
};

const SearchForm = React.forwardRef((props, ref) => {
  const {
    searchButtonRef,
    browseButtonRef,
    searchContentDrawerRef
  } = ref;
  let placeholder = 'What are you looking for?'; // Unauthenticated Users
  const {
    isAuthenticated,
    isEducator,
  } = props.userProperties;

  if (isAuthenticated) {
    placeholder = (
      isEducator ?
        'What do you want to teach?' : // Educators
        'What do you want to read?' // Students
    );
  }

  // Apply focus to the form's input-field when the component mounts.
  const inputFieldRef = useRef();
  useLayoutEffect(() => {
    if (inputFieldRef.current) {
      // We need this timeout so on mobile the animation doesn't jump
      setTimeout(() => {
        inputFieldRef.current.focus();
      }, animationTimeout);
    }
  }, []);

  // Handle changes to the query term input value.
  const onInputChange = (event) => {
    props.setQueryTerm(event.target.value);

    // Don't display automated suggestions for blank inputs (they evaluate to
    // empty strings) that are created when the space bar is pressed.
    const queryIsPresent = Boolean(trim(event.target.value));
    props.setVisibilityOfSuggestions(queryIsPresent);
  };

  // Handle keydown events in the search form's input field.
  const onKeyDownForInputField = (event) => {
    if (event.keyCode === ENTER) {
      props.onQueryTermSubmission(props.queryTerm);
    } else if (event.keyCode === ESCAPE) {
      props.handleOnEscape(searchContentDrawerRef, searchButtonRef);
    } else if (event.keyCode === DOWN_ARROW) {
      // Compile a list of the <li/> tags that represent search suggestions
      // and insert a data attribute ('data-index-number')
      // into each tag. This 'data-index-number' attribute will reflect the
      // tag's index number. This value will be used to handle keyboard events.
      const suggestions = searchContentDrawerRef.current.getElementsByTagName('li');

      forEach(suggestions, (suggestion, index) => {
        suggestion.setAttribute('data-index-number', index);
      });

      // When a user hits the 'down-arrow' key, apply focus to the first
      // suggestion. A seperate 'onKeyDown' function will handle the list's
      // subsequent keyboard events.
      if (suggestions.length > 0) {
        suggestions[0].focus();
      }
    }
  };

  const onDeleteInput = () => {
    props.setQueryTerm('');
    props.setThrottledQueryTerm('');
    props.setVisibilityOfSuggestions(false);
  };

  const onKeyDownForFaTimesIcon = (event) => {
    if (event.keyCode === ENTER) {
      event.preventDefault();

      onDeleteInput();

      // Apply focus to the input field.
      inputFieldRef.current.focus();
    } else if (event.keyCode === TAB) {
      props.handleOnCancel(searchButtonRef);
    }
  };

  const queryIsPresent = Boolean(trim(props.queryTerm));

  const onKeyDownForCancelButton = (event) => {
    // The check for the shift key handles the 'shift+tab' key combo event.
    // A 'tab' event brings focus to the Browse button. A 'shift+tab' key
    // event brings focus to the preceding hyperlink: the search form's 'Search'
    // button.
    if (event.keyCode === TAB & !event.shiftKey) {
      props.handleOnCancel(browseButtonRef);
    }
  };

  return (
    <form
      className={css(styles.searchForm(props.isDesktopDisplay))}
    >
      <i className={`fa fa-search ${css(styles.searchIcon())}`}/>
      <input
        aria-label="Search Newsela"
        autoComplete="off"
        className={css(styles.searchInput(props.isDesktopDisplay))}
        data-qa-selector="query_term_input"
        elementtiming="query-term-input"
        id={GLOBAL_NAV_SEARCH_INPUT_FIELD_ID}
        maxLength="200"
        onChange={onInputChange}
        onPaste={onKeyDownForInputField}
        onKeyDown={onKeyDownForInputField}
        onSubmit={props.onQueryTermSubmission}
        placeholder={placeholder}
        ref={inputFieldRef}
        type="text"
        value={props.queryTerm}
      />
      {
        props.isDesktopDisplay &&
        <AngelouButtonWithTrackEvent
          flavor={Button.flavor.solid}
          statusColor={Button.statusColor.primary}
          onClick={() => props.onQueryTermSubmission(props.queryTerm)}
          ariaProps={{
            'aria-label': 'Submit Search Term'
          }}
          objectType="Content"
          actionName="Search"
          legacyEventName="Submit Search Term"
          streamProperties={{
            search_term: props.queryTerm,
          }}
          dataProps={{
            'data-qa-selector': 'query_term_submit_btn'
          }}
          __classNameFor={{root: 'globalNavSearchSubmitButton'}}
          __cssFor={{
            root: `
              border-radius: 4px;
              margin-right: 4px;
            `
          }}
        >
          Search
        </AngelouButtonWithTrackEvent>
      }
      {
        props.isDesktopDisplay &&
        <ButtonWithTrackEvent
          flat
          secondary
          onClick={() => props.handleOnCancel(searchButtonRef)}
          aria-label="Cancel Search"
          className={css(styles.cancelButton)}
          onKeyDown={onKeyDownForCancelButton}
          ariaLabel="Cancel Search"
          objectType="Content"
          actionPrefix="Cancel"
          actionName="Search"
          legacyEventName="Cancel Search"
          label={<span>Cancel</span>}
        />
      }
      {
        !props.isDesktopDisplay &&
        queryIsPresent &&
        <ButtonWithTrackEvent
          rounded
          secondary
          className={styles.timesIconContainer}
          onClick={onDeleteInput}
          onKeyDown={onKeyDownForFaTimesIcon}
          aria-label="Delete Search Input"
          objectType="Content"
          actionPrefix="Cancel"
          actionName="Search"
          legacyEventName="Delete Search Input"
          label={
            <FontAwesomeIcon
              icon={faTimes}
              className={styles.timesIcon}
            />
          }
        />
      }
    </form>
  );
});

SearchForm.propTypes = propTypes;
SearchForm.defaultProps = defaultProps;
export default SearchForm;
